import {noop} from 'lodash';
import {useRef} from 'react';

import {HtmlEditor} from '../../../common/editor/html/htmlEditor';

export type FormattingOperation = (htmlEditor: HtmlEditor) => void;

interface ApplyFormattingOptions {
  htmlEditor: HtmlEditor;
  operation: FormattingOperation;
  isRepeatable: boolean;
  hasRepeatFormatting: boolean;
  lastFormattingOperation: FormattingOperation | undefined;
  lastFormattingChainText: string | undefined;
}

export function applyFormattingToEditor(options: ApplyFormattingOptions) {
  const {htmlEditor, operation, isRepeatable, hasRepeatFormatting} = options;
  let {lastFormattingChainText, lastFormattingOperation} = options;
  operation(htmlEditor);
  htmlEditor.focus();

  if (!hasRepeatFormatting) {
    return;
  }

  if (!isRepeatable) {
    lastFormattingOperation = undefined;
    lastFormattingChainText = undefined;
    return;
  }

  // If the selected text hasn't changed, chain this formatting operation to the previous one.
  const {selectedText} = htmlEditor.services.selection;
  const chainedOperation: FormattingOperation =
    lastFormattingOperation && lastFormattingChainText && lastFormattingChainText === selectedText
      ? lastFormattingOperation
      : noop;

  lastFormattingOperation = (editor) => {
    chainedOperation(editor);
    operation(editor);
  };
  lastFormattingChainText = selectedText;
}

export function useApplyFormattingToEditor(htmlEditor: HtmlEditor, hasRepeatFormatting: boolean) {
  const lastFormattingOperation = useRef<FormattingOperation | undefined>();
  const lastFormattingChainText = useRef<string | undefined>();

  return (operation: FormattingOperation, isRepeatable: boolean) => {
    applyFormattingToEditor({
      htmlEditor,
      hasRepeatFormatting,
      lastFormattingChainText: lastFormattingChainText.current,
      lastFormattingOperation: lastFormattingOperation.current,
      operation,
      isRepeatable,
    });
  };
}
